/* ---- Home: intro headline "jump up" ----
 * The intro (.floating-text.banner-hl) stays put while the page starts to
 * scroll and leaves in one deliberate move once the "Projekte" heading has
 * come up a quarter of the viewport:
 *
 *     hold (pin)  →  "Projekte" reaches EXIT_START  →  jump up / fade out
 *
 * Two separate ScrollTriggers do this:
 *   - a pin without spacing keeps the intro where it is. No scroll distance is
 *     added, so nothing below it shifts. ScrollTrigger picks a transform pin
 *     under ScrollSmoother (mouse) and position:fixed with native scrolling
 *     (touch) by itself.
 *   - a trigger on the heading plays a time-based timeline and reverses it
 *     when scrolling back over the same line. It is not scrubbed: the move has
 *     its own timing, the scroll position only decides when it starts.
 *
 * The timeline moves the two lines (#static-strings / #typed-strings), never
 * the pinned element (its transform belongs to the pin) and never the words or
 * characters inside (their y/opacity belong to animateTypedWords() and to the
 * CSS `float` animation).
 * Markup: page-templates/start.php.
 */
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

/* Where the top of the "Projekte" heading has to be for the intro to leave */
const EXIT_START = 'top 75%';
/* How far the lines travel, as a share of the viewport height */
const JUMP_DISTANCE = 0.45;
const ANTICIPATION = 10;
const LINE_STAGGER = 0.06;

let instance = null;

export function initHomeIntro() {
  destroyHomeIntro();

  const intro = document.querySelector('.home .floating-text.banner-hl');
  const banner = document.getElementById('banner');
  /* The heading is optional (empty ACF field) – the section always exists */
  const heading = document.querySelector('#projekte h2') || document.getElementById('projekte');
  if (!intro || !banner || !heading) return;

  let lines = Array.from(intro.querySelectorAll('#static-strings, #typed-strings'));
  if (!lines.length) lines = Array.from(intro.children);
  if (!lines.length) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const timeline = gsap.timeline({ paused: true });

  if (reducedMotion) {
    timeline.to(lines, { autoAlpha: 0, duration: 0.3, ease: 'none' });
  } else {
    gsap.set(lines, { transformOrigin: '50% 100%' });
    timeline
      /* Short dip before the jump */
      .to(lines, {
        y: ANTICIPATION,
        duration: 0.14,
        ease: 'power2.out',
        stagger: LINE_STAGGER
      })
      .to(lines, {
        y: () => -window.innerHeight * JUMP_DISTANCE,
        scaleY: 1.04,
        duration: 0.5,
        ease: 'power3.in',
        stagger: LINE_STAGGER
      }, 0.14)
      /* Fade late, so the move reads as a jump and not as a fade-out */
      .to(lines, {
        autoAlpha: 0,
        duration: 0.3,
        ease: 'power1.in',
        stagger: LINE_STAGGER
      }, 0.34);
  }

  const pin = ScrollTrigger.create({
    trigger: banner,
    start: 0,
    end: 'bottom top',
    pin: intro,
    pinSpacing: false
  });

  const exit = ScrollTrigger.create({
    trigger: heading,
    start: EXIT_START,
    animation: timeline,
    toggleActions: 'play none none reverse',
    invalidateOnRefresh: true
  });

  instance = { timeline, pin, exit, lines };
}

export function destroyHomeIntro() {
  if (!instance) return;

  instance.exit.kill();
  instance.pin.kill();
  instance.timeline.kill();
  gsap.set(instance.lines, { clearProps: 'transform,transformOrigin,opacity,visibility' });
  instance = null;
}
